import React, { useState, useEffect } from 'react';
import { Users, Clock, ArrowRight, Activity } from 'lucide-react';
import apiService from './apiService';
import useWebSocket from './hooks/useWebSocket';

/**
 * Queue Display Board - For TV/Monitor Display
 * Shows current patient being served and waiting queue
 * Real-time updates via WebSocket
 * Large text for visibility from distance
 */
const QueueDisplayBoard = ({ location = 'consultation', facilityName = 'Hospital' }) => {
    const [queueData, setQueueData] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [loading, setLoading] = useState(true);

    // Fetch queue data
    const fetchQueue = async () => {
        try {
            const response = await apiService.getQueueByLocation(location);
            setQueueData(response);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching queue:', error);
        }
    };

    // Initial load
    useEffect(() => {
        fetchQueue();
    }, [location]);

    // WebSocket for real-time updates
    useWebSocket({
        onMessage: (message) => {
            // Refresh queue when any queue update occurs
            if (message.type === 'queue_updated') {
                console.log('Queue updated via WebSocket, refreshing display...');
                fetchQueue();
            }
        },
        onConnect: () => {
            console.log('Queue display board connected to real-time updates');
        }
    });

    // Update clock every second
    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    const getLocationDisplay = (loc) => {
        const locations = {
            'reception': 'Reception',
            'triage': 'Triage',
            'consultation': 'Consultation',
            'lab': 'Laboratory',
            'pharmacy': 'Pharmacy',
            'billing': 'Billing'
        };
        return locations[loc] || loc;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
                <div className="text-white text-4xl">Loading Queue...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 p-8">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-5xl font-bold text-gray-900 mb-2">{facilityName}</h1>
                        <h2 className="text-3xl text-blue-600 font-semibold">{getLocationDisplay(location)}</h2>
                    </div>
                    <div className="text-right">
                        <div className="text-6xl font-bold text-gray-900">
                            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-2xl text-gray-600 mt-2">
                            {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Now Serving */}
            <div className="bg-white rounded-2xl shadow-2xl p-12 mb-8">
                <div className="flex items-center gap-4 mb-6">
                    <Activity className="w-12 h-12 text-green-500" />
                    <h3 className="text-4xl font-bold text-gray-700">NOW SERVING</h3>
                </div>
                
                {queueData?.nowServing ? (
                    <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-12 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-8xl font-bold mb-4">
                                    {String(queueData.nowServing.queueNumber).padStart(3, '0')}
                                </div>
                                <div className="text-4xl font-semibold">
                                    {queueData.nowServing.name}
                                </div>
                            </div>
                            {queueData.nowServing.roomNumber && (
                                <div className="text-right">
                                    <div className="text-3xl text-green-100 mb-2">PROCEED TO</div>
                                    <div className="text-6xl font-bold">
                                        {queueData.nowServing.roomNumber}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="bg-gray-100 rounded-xl p-12 text-center">
                        <div className="text-5xl text-gray-400 font-semibold">
                            No Patient Currently Being Served
                        </div>
                    </div>
                )}
            </div>

            {/* Waiting Queue */}
            <div className="bg-white rounded-2xl shadow-2xl p-12">
                <div className="flex items-center gap-4 mb-8">
                    <Users className="w-12 h-12 text-blue-500" />
                    <h3 className="text-4xl font-bold text-gray-700">
                        WAITING QUEUE ({queueData?.totalWaiting || 0})
                    </h3>
                </div>
                
                {queueData?.waitingQueue && queueData.waitingQueue.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                        {queueData.waitingQueue.slice(0, 12).map((patient, index) => (
                            <div 
                                key={patient.id}
                                className={`rounded-xl p-6 text-center transition-all ${
                                    index === 0 ? 'bg-yellow-100 border-4 border-yellow-500 scale-110' : 'bg-blue-50 border-2 border-blue-200'
                                }`}
                            >
                                <div className={`text-5xl font-bold mb-2 ${
                                    index === 0 ? 'text-yellow-700' : 'text-blue-700'
                                }`}>
                                    {String(patient.queueNumber).padStart(3, '0')}
                                </div>
                                {index === 0 && (
                                    <div className="text-xl font-semibold text-yellow-700">NEXT</div>
                                )}
                            </div>
                        ))}
                        
                        {queueData.waitingQueue.length > 12 && (
                            <div className="rounded-xl p-6 bg-gray-100 text-center flex items-center justify-center">
                                <div className="text-4xl font-bold text-gray-600">
                                    +{queueData.waitingQueue.length - 12}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-gray-100 rounded-xl p-12 text-center">
                        <div className="text-4xl text-gray-400 font-semibold">
                            No Patients Waiting
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="mt-8 text-center text-white text-2xl">
                <p>Please wait for your number to be called</p>
                <p className="text-xl text-blue-200 mt-2">Thank you for your patience</p>
            </div>
        </div>
    );
};

export default QueueDisplayBoard;

